/**
 * Signal (信号) 类
 * 一种比 EventEmitter 更轻量、高效的事件分发机制
 */
export class Signal {
    constructor() {
        // 存储所有绑定的监听器对象
        this._bindings = [];
    }

    /**
     * 添加监听器
     * @param {Function} listener - 回调函数
     * @param {Object} scope - 回调函数的执行上下文 (this)
     * @param {boolean} once - 是否只触发一次
     * @returns {SignalBinding} 返回一个绑定对象，方便后续移除
     */
    add(listener, scope = null, once = false) {
        if (typeof listener !== 'function') {
            throw new Error('listener 必须是一个函数');
        }

        // 检查是否已经存在相同的绑定，防止重复添加
        let binding = this._getBinding(listener, scope);
        
        if (!binding) {
            binding = new SignalBinding(this, listener, scope, once);
            this._bindings.push(binding);
        }

        return binding;
    }

    /**
     * 添加只执行一次的监听器
     */
    addOnce(listener, scope = null) {
        return this.add(listener, scope, true);
    }

    /**
     * 分发/触发信号
     * @param {...any} args - 传递给监听器的参数
     */
    dispatch(...args) {
        // 复制一份列表，防止在执行过程中有新绑定加入或删除导致逻辑混乱
        const internalBindings = [...this._bindings];
        
        internalBindings.forEach(binding => {
            binding.execute(args);
        });
    }

    /**
     * 移除特定监听器
     */
    remove(listener, scope = null) {
        const binding = this._getBinding(listener, scope);
        if (binding) {
            binding.detach();
        }
    }

    /**
     * 内部方法：查找现有的绑定
     */
    _getBinding(listener, scope) {
        return this._bindings.find(b => b.listener === listener && b.scope === scope);
    }

    /**
     * 内部方法：由 SignalBinding 调用，从列表中彻底移除
     */
    _destroyBinding(binding) {
        const index = this._bindings.indexOf(binding);
        if (index !== -1) {
            this._bindings.splice(index, 1);
        }
    }
}

/**
 * SignalBinding (信号绑定) 类
 * 对应你代码中使用的 .detach() 方法
 */
class SignalBinding {
    constructor(signal, listener, scope, once) {
        this.signal = signal;
        this.listener = listener;
        this.scope = scope;
        this.once = once;
    }

    /**
     * 执行回调
     */
    execute(args) {
        this.listener.apply(this.scope, args);
        if (this.once) {
            this.detach();
        }
    }

    /**
     * 断开连接：对应代码中的 .detach()
     */
    detach() {
        if (this.signal) {
            this.signal._destroyBinding(this);
            this.signal = null; // 防止多次销毁
        }
    }
}